import type { SearchParams, Location } from '../types'

import { Button } from 'antd'

import { PushType } from '../../../domain/push-type.enum'
import { ordinal, pluralize } from '../formatters'

const areFiltersEqual = (current: SearchParams, filters: Partial<SearchParams>): boolean => {
  for (const key in filters) {
    const filterValue = filters[key as keyof SearchParams]
    const currentValue = current[key as keyof SearchParams]
    if (Array.isArray(filterValue) && Array.isArray(currentValue)) {
      if (
        filterValue.length !== currentValue.length ||
        !filterValue.every((val, idx) => val === currentValue[idx])
      ) {
        return false
      }
    } else if (filterValue !== currentValue) {
      return false
    }
  }
  return true
}

export interface LocationScheduledDayFilter {
  location: string
  schedule_day: number
  count: number
}

interface QuickFilterProps {
  filter: LocationScheduledDayFilter
  searchParams: Partial<SearchParams>
  locations: Location[]
  onClick: (filter: Partial<SearchParams>) => void
  currentSearchParams: SearchParams
}

export const QuickFilter = (props: QuickFilterProps): JSX.Element => {
  const { filter, searchParams, currentSearchParams } = props

  const location = props.locations.find(l => l.id === filter.location)!

  const label = `${location.short_name} - ${ordinal(filter.schedule_day)}`

  const selected = areFiltersEqual(currentSearchParams, searchParams)

  return (
    <div
      className={`border rounded-md p-2.5 flex items-center justify-between min-w-[150px] text-[12px] ${selected ? 'bg-sky-100' : ''}`}
    >
      <div>
        <span className='font-semibold block'>{label}</span>
        <span className='text-gray-500'>
          {filter.count}
          {pluralize(filter.count, ' item', ' items')}
        </span>
      </div>
      <div>
        <Button
          type='link'
          onClick={() => {
            props.onClick(searchParams)
          }}
        >
          Apply
        </Button>
      </div>
    </div>
  )
}

interface QuickFiltersProps {
  filters?: LocationScheduledDayFilter[]
  locations: Location[]
  onClick: (filter: Partial<SearchParams>) => void
  currentSearchParams: SearchParams
}

export const QuickFilters = (props: QuickFiltersProps): JSX.Element => {
  return (
    <div className='overflow-x-auto w-full'>
      <div className='flex flex-row gap-2 items-center p-4 w-full max-w-[100px]'>
        {props.filters?.map(filter => (
          <QuickFilter
            key={`${filter.location}-${filter.schedule_day}`}
            filter={filter}
            searchParams={{
              locationIds: [filter.location],
              schedule: [filter.schedule_day],
              fundingTypes: [PushType.Pull],
              openAr: ['positive'],
              flags: ['has-open-invoices']
            }}
            locations={props.locations}
            onClick={props.onClick}
            currentSearchParams={props.currentSearchParams}
          />
        ))}
      </div>
    </div>
  )
}
